import { useEffect } from 'react';
import * as v from 'valibot';

import { createFileRoute, stripSearchParams } from '@tanstack/react-router';

import { faGhost } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, Container, Typography } from '@mui/material';

import { useUser } from '@repo/auth';

import { saveHomeSearchSnapshot } from '../../-context/homeSearchStorage';
import { RisksTable } from './-components/RisksTable';
import { RiskStats } from './-components/RiskStats';

const defaultFilters = {
	page: 0,
	rowsPerPage: 10,
	riskStatusIds: [],
	underwriterIds: [],
	searchText: '',
	showClosed: false,
};

export const Filters = v.object({
	page: v.optional(v.number(), defaultFilters.page),
	rowsPerPage: v.optional(v.number(), defaultFilters.rowsPerPage),
	riskStatusIds: v.optional(
		v.fallback(v.array(v.number()), defaultFilters.riskStatusIds),
	),
	underwriterIds: v.optional(
		v.fallback(v.array(v.string()), defaultFilters.underwriterIds),
	),
	fromRiskCreated: v.optional(v.string()),
	toRiskCreated: v.optional(v.string()),
	searchText: v.optional(v.string(), defaultFilters.searchText),
	showClosed: v.optional(v.boolean(), defaultFilters.showClosed),
});

export const Route = createFileRoute('/_risks/risks/')({
	component: function HomePage() {
		const { permissions } = useUser();
		const search = Route.useSearch();

		useEffect(() => {
			saveHomeSearchSnapshot('risks', search);
		}, [search]);

		const canViewDashboard = permissions.includes('home.risk.dashboard');
		const canViewRisksTable = permissions.includes('home.risk.table');
		return (
			<>
				{canViewDashboard && (
					<Container
						disableGutters
						maxWidth="lg"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							mb: 2,
							gap: 1,
							mt: 2,
						}}
					>
						<Typography variant="h6" component="h1">
							Risk Data
						</Typography>
						<RiskStats />
					</Container>
				)}
				{canViewRisksTable && (
					<Container
						maxWidth="lg"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							backgroundColor: 'offwhite.light',
							borderRadius: 2,
							paddingTop: 2,
						}}
					>
						<Typography variant="h5">All Risks</Typography>
						<RisksTable />
					</Container>
				)}
				{!canViewDashboard && !canViewRisksTable && (
					<Container
						maxWidth="lg"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							backgroundColor: 'offwhite.light',
							pt: 2,
							mt: 2,
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								p: 4,
								gap: 1,
								position: 'relative',
								m: 'auto',
							}}
						>
							<FontAwesomeIcon color="#cccccc" icon={faGhost} size="2xl" />
						</Box>
					</Container>
				)}
			</>
		);
	},
	validateSearch: Filters,
	search: {
		middlewares: [stripSearchParams(defaultFilters)],
	},
	head: (ctx) => ctx.match.context.setTitle('Risks', 'Home'),
});
